import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, ShieldCheck, Upload, FileText, Check, AlertTriangle, Send, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOR = {
  not_started: 'bg-slate-100 text-slate-600',
  documents_uploaded: 'bg-blue-100 text-blue-700',
  under_review: 'bg-amber-100 text-amber-700',
  verified: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

export default function PropertyVerificationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [property, setProperty] = useState(null);
  const [verification, setVerification] = useState(null);
  const [docMeta, setDocMeta] = useState({});
  const [mandatory, setMandatory] = useState([]);
  const [optional, setOptional] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingKey, setUploadingKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [skuPrice, setSkuPrice] = useState(null);

  const tt = (en, es) => (language === 'es' ? es : en);

  const load = async () => {
    try {
      const [pRes, vRes, tRes, cfgRes] = await Promise.all([
        axios.get(`${API}/properties/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/property-verification/property/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/property-verification/types`),
        axios.get(`${API}/payments/config/public`).catch(() => ({ data: { skus: {} } })),
      ]);
      setProperty(pRes.data);
      setVerification(vRes.data);
      setDocMeta(tRes.data.documents);
      setMandatory(tRes.data.mandatory);
      setOptional(tRes.data.optional);
      const cents = cfgRes.data?.skus?.property_verification?.price_usd_cents;
      setSkuPrice(cents != null ? cents / 100 : null);
    } catch {
      toast.error(tt('Failed to load', 'Error al cargar'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Owner-only guard (admin too)
  useEffect(() => {
    if (loading || !property || !user) return;
    const isOwner = property.owner_id === user.id;
    const isAdmin = ['admin', 'superadmin'].includes(user.role);
    if (!isOwner && !isAdmin) {
      toast.error(tt('Only the property owner can verify this listing', 'Solo el propietario puede verificar esta propiedad'));
      navigate(`/property/${id}`);
    }
  }, [loading, property, user, id, navigate, tt]);

  const handleUpload = async (docType, file) => {
    if (!file) return;
    setUploadingKey(docType);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const upRes = await axios.post(`${API}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const url = upRes.data.url || upRes.data.path;
      await axios.post(
        `${API}/property-verification/property/${id}/upload`,
        { doc_type: docType, url, file_name: file.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(tt('Document uploaded', 'Documento cargado'));
      await load();
    } catch (e) {
      toast.error(tt('Upload failed', 'Error al cargar el archivo'));
    } finally {
      setUploadingKey('');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/property-verification/property/${id}/submit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(tt('Submitted for admin review!', '¡Enviado para revisión!'));
      await load();
    } catch (e) {
      const msg = e.response?.data?.detail || tt('Submit failed', 'Error al enviar');
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-3" data-testid="prop-verify-loading">
        <Skeleton className="h-32 rounded-xl" />
        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
      </div>
    );
  }

  const status = verification?.status || 'not_started';
  const uploadedDocs = verification?.uploaded_docs || [];
  const documents = verification?.documents || {};
  const mandatoryComplete = mandatory.every(k => uploadedDocs.includes(k));
  const propPaymentStatus = property?.verification_status || '';
  const hasPaid = propPaymentStatus === 'paid_pending_review' || propPaymentStatus === 'verified' || status === 'verified';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="prop-verification-page">
      <button
        type="button"
        onClick={() => {
          if (user?.role === 'agent') navigate('/agency');
          else if (user?.role === 'lawyer') navigate('/lawyer/dashboard');
          else navigate(`/property/${id}`);
        }}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#D4AF37] mb-4"
        data-testid="back-to-property"
      >
        <ArrowLeft className="w-4 h-4" />
        {user?.role === 'agent' || user?.role === 'lawyer'
          ? tt('Back to dashboard', 'Volver al panel')
          : tt('Back to property', 'Volver a la propiedad')}
      </button>

      {/* Header card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B192C] to-[#1B2A4E] text-white p-6 mb-6" data-testid="verify-header">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/20 border border-[#D4AF37] flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-medium mb-1">{tt('Property Verification', 'Verificación de Propiedad')}</p>
            <h1 className="font-heading text-xl md:text-2xl font-bold">{property?.title}</h1>
            <p className="text-sm text-slate-300 mt-2 max-w-2xl">
              {tt(
                'Upload the legal documents below. WakaCasa runs an AI pre-check on each, then our admin team reviews and approves. Once verified, your property carries the WakaCasa-Verified seal — and Premium buyers can preview the documents.',
                'Sube los documentos legales abajo. WakaCasa hace una pre-revisión con IA y nuestro equipo aprueba. Una vez verificada, tu propiedad lleva el sello WakaCasa-Verificado y los compradores Premium pueden ver los documentos.'
              )}
            </p>
          </div>
          <Badge className={`${STATUS_COLOR[status]} text-xs uppercase tracking-wider px-3 py-1`} data-testid="verify-status-badge">
            {tt(status.replace('_', ' '), status.replace('_', ' '))}
          </Badge>
        </div>
      </div>

      {/* Pricing CTA — show only when property is NOT yet verified */}
      {status !== 'verified' && (
        <Card className="border-2 border-[#D4AF37]/40 bg-gradient-to-br from-amber-50 to-white mb-6" data-testid="property-verify-pricing-cta">
          <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-[#0B192C]" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-[#0B192C]">{tt('Verification Fee — One-time', 'Tarifa de Verificación — Pago Único')}</p>
                <p className="text-xs text-slate-600 mt-0.5 max-w-md">
                  {tt('Get the gold Verified badge on this property. Boosts trust & search ranking. Pay by Bank Transfer, Pago Móvil, or Zelle.', 'Obtén el sello dorado Verificado en esta propiedad. Aumenta la confianza y el ranking. Paga por Transferencia, Pago Móvil o Zelle.')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right">
                <div className="font-heading text-2xl font-bold text-[#0B192C]" data-testid="property-verify-price">
                  USD ${skuPrice != null ? skuPrice.toFixed(0) : '49'}
                </div>
                <div className="text-[10px] text-slate-500">{tt('one-time', 'pago único')}</div>
              </div>
              <Button
                onClick={() => navigate(`/payments/checkout?sku=property_verification&target_id=${id}`)}
                className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold"
                data-testid="property-verify-pay-btn"
              >
                {tt('Pay verification →', 'Pagar verificación →')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Paid — Pending Admin Review banner */}
      {hasPaid && status !== 'verified' && (
        <Card className="border-2 border-amber-300 bg-amber-50 mb-6" data-testid="property-verify-paid-pending">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-200 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-amber-700" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-amber-900 text-sm">
                {tt('Payment received — Pending Verification', 'Pago recibido — Verificación Pendiente')}
              </p>
              <p className="text-xs text-amber-800 mt-1 max-w-2xl">
                {tt(
                  'Your property now shows a "Pending Verification" badge to buyers. Upload all mandatory documents below — admin will review them and upgrade you to the full gold Verified badge.',
                  'Tu propiedad ya muestra una insignia "Verificación Pendiente" a los compradores. Sube todos los documentos obligatorios — un admin los revisará y otorgará la insignia dorada Verificada.'
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload + submit sections — gated behind approved payment */}
      {!hasPaid ? (
        <Card className="border-2 border-slate-200 bg-slate-50 mb-6" data-testid="property-upload-gate">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 mx-auto rounded-2xl bg-slate-200 flex items-center justify-center mb-3">
              <ShieldCheck className="w-6 h-6 text-slate-400" />
            </div>
            <p className="font-semibold text-[#0B192C]">
              {tt('Document upload unlocks after payment', 'La carga de documentos se habilita tras el pago')}
            </p>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              {tt(
                'Pay the verification fee above to unlock the document upload form. Until then, no documents can be submitted.',
                'Paga la tarifa de verificación arriba para habilitar el formulario de carga. Hasta entonces, no se pueden enviar documentos.'
              )}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mandatory Section */}
          <h2 className="font-heading text-lg font-semibold text-[#0B192C] mb-3 flex items-center gap-2">
            <Check className="w-4 h-4 text-[#D4AF37]" />
            {tt('Mandatory Documents', 'Documentos Obligatorios')}
          </h2>
          <div className="space-y-3 mb-8">
            {mandatory.map(key => (
              <DocRow key={key} docKey={key} meta={docMeta[key]} doc={documents[key]} uploadingKey={uploadingKey} onUpload={handleUpload} status={status} language={language} />
            ))}
          </div>

          {/* Optional Section */}
          <h2 className="font-heading text-lg font-semibold text-[#0B192C] mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            {tt('Optional Documents', 'Documentos Opcionales')}
            <span className="text-xs text-slate-400 font-normal ml-1">({tt('Stronger verification', 'Verificación más fuerte')})</span>
          </h2>
          <div className="space-y-3 mb-8">
            {optional.map(key => (
              <DocRow key={key} docKey={key} meta={docMeta[key]} doc={documents[key]} uploadingKey={uploadingKey} onUpload={handleUpload} status={status} language={language} />
            ))}
          </div>

          {/* Submit CTA */}
          {status === 'not_started' || status === 'documents_uploaded' ? (
            <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4 flex-wrap" data-testid="submit-section">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-[#0B192C]">{tt('Ready to submit?', '¿Listo para enviar?')}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {mandatoryComplete
                    ? tt('All mandatory documents uploaded. Submit for admin review.', 'Todos los obligatorios cargados. Envía para revisión.')
                    : tt('Upload all mandatory documents to enable submission.', 'Sube todos los obligatorios para enviar.')}
                </p>
              </div>
              <Button onClick={handleSubmit} disabled={!mandatoryComplete || submitting}
                className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-2 disabled:opacity-50"
                data-testid="submit-verification-btn">
                <Send className="w-4 h-4" />
                {submitting ? tt('Submitting…', 'Enviando…') : tt('Submit for Review', 'Enviar para Revisión')}
              </Button>
            </div>
          ) : status === 'under_review' ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800" data-testid="under-review-banner">
              <strong>{tt('Under review', 'En revisión')}.</strong> {tt('Our admin team is reviewing your documents. We\'ll notify you once approved.', 'Nuestro equipo está revisando tus documentos. Te avisaremos cuando se apruebe.')}
            </div>
          ) : status === 'verified' ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800 flex items-center gap-2" data-testid="verified-banner">
              <ShieldCheck className="w-5 h-5" />
              <strong>{tt('Verified!', '¡Verificada!')}</strong> {tt('Your property now carries the WakaCasa-Verified seal.', 'Tu propiedad ahora lleva el sello WakaCasa-Verificado.')}
            </div>
          ) : status === 'rejected' ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800" data-testid="rejected-banner">
              <strong>{tt('Rejected.', 'Rechazada.')}</strong> {verification?.admin_notes && <span className="block mt-1 text-xs">{verification.admin_notes}</span>}
              <p className="mt-2">{tt('Re-upload corrected documents and submit again.', 'Vuelve a subir los documentos corregidos y envíalos.')}</p>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function DocRow({ docKey, meta, doc, uploadingKey, onUpload, status, language }) {
  const tt = (en, es) => (language === 'es' ? es : en);
  const uploaded = !!doc;
  const aiStatus = doc?.ai_status;
  const aiResult = doc?.ai_result;
  const locked = ['under_review', 'verified'].includes(status);

  return (
    <Card className={uploaded ? 'border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/5 to-transparent' : ''} data-testid={`doc-row-${docKey}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${uploaded ? 'bg-[#D4AF37]/15 text-[#0B192C]' : 'bg-slate-100 text-slate-400'}`}>
            <FileText className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-sm text-[#0B192C]" data-testid={`doc-label-${docKey}`}>
                {language === 'es' ? meta?.label_es : meta?.label_en}
              </h3>
              {meta?.mandatory && <Badge className="bg-red-50 text-red-600 text-[10px] hover:bg-red-50 border border-red-200" data-testid={`doc-mandatory-${docKey}`}>{tt('Required', 'Requerido')}</Badge>}
              {uploaded && <Badge className="bg-green-50 text-green-700 text-[10px] hover:bg-green-50 border border-green-200 gap-0.5"><Check className="w-2.5 h-2.5" />{tt('Uploaded', 'Cargado')}</Badge>}
              {aiStatus === 'running' && <Badge className="bg-blue-50 text-blue-700 text-[10px] hover:bg-blue-50">{tt('AI running…', 'IA procesando…')}</Badge>}
              {aiStatus === 'complete' && aiResult?.valid && <Badge className="bg-[#D4AF37]/20 text-[#0B192C] text-[10px] hover:bg-[#D4AF37]/20 gap-0.5"><Sparkles className="w-2.5 h-2.5" />{tt('AI passed', 'IA aprobada')}</Badge>}
              {aiStatus === 'complete' && aiResult && aiResult.valid === false && <Badge className="bg-red-50 text-red-700 text-[10px] hover:bg-red-50 gap-0.5"><X className="w-2.5 h-2.5" />{tt('AI flagged', 'IA marcó')}</Badge>}
            </div>
            <p className="text-xs text-slate-500 mt-1">{language === 'es' ? meta?.description_es : meta?.description_en}</p>
            {uploaded && doc?.file_name && (
              <p className="text-[11px] text-slate-400 mt-1 truncate" data-testid={`doc-filename-${docKey}`}>{doc.file_name}</p>
            )}
            {aiResult?.summary && (
              <p className="text-[11px] text-slate-600 mt-1 italic">"{aiResult.summary}"</p>
            )}
            {aiResult?.concerns && (
              <p className="text-[11px] text-amber-700 mt-0.5 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{aiResult.concerns}</p>
            )}
          </div>
          {!locked && (
            <div className="flex-shrink-0">
              <input
                id={`file-${docKey}`}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => onUpload(docKey, e.target.files?.[0])}
                disabled={!!uploadingKey}
                data-testid={`doc-input-${docKey}`}
              />
              <Button asChild variant={uploaded ? 'outline' : 'default'} size="sm" className={`gap-1.5 ${uploaded ? '' : 'bg-[#0B192C] hover:bg-[#1B2A4E] text-white'}`} disabled={!!uploadingKey}>
                <label htmlFor={`file-${docKey}`} className="cursor-pointer inline-flex items-center gap-1.5" data-testid={`doc-upload-btn-${docKey}`}>
                  <Upload className="w-3.5 h-3.5" />
                  {uploadingKey === docKey ? tt('Uploading…', 'Subiendo…') : uploaded ? tt('Replace', 'Reemplazar') : tt('Upload', 'Subir')}
                </label>
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
